import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Search, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { otpReleased, useOTPInfo } from '@/lib/otp';
import { cn } from '@/lib/utils';

// Biblioteca de modelos da Meta: textos de utilidade e autenticação que ela já
// aprovou. Criado sem mexer no texto, o template nasce APROVADO — sem as 24 horas
// de análise. É o caminho de menor esforço para o cliente ter o template que
// precisa: escolher, preencher o link ou telefone do botão, criar.

interface LibButton { type: string; text?: string; url?: string; phone_number?: string }
interface LibTemplate {
  id: string; name: string; language: string; category: string;
  topic?: string; usecase?: string; header?: string; body: string; body_params?: string[];
  buttons?: LibButton[];
}

type Cat = 'UTILITY' | 'AUTHENTICATION';
const LANGS = ['pt_BR', 'en_US', 'es'];

/** O corpo com cada {{n}} trocado pelo exemplo da Meta, destacado. */
function BodyPreview({ body, params }: { body: string; params?: string[] }) {
  const parts = body.split(/(\{\{\d+\}\})/g);
  return (
    <p className="whitespace-pre-wrap text-sm">
      {parts.map((p, i) => {
        const m = p.match(/^\{\{(\d+)\}\}$/);
        if (!m) return <span key={i}>{p}</span>;
        const ex = params?.[Number(m[1]) - 1];
        return <mark key={i} className="rounded bg-primary/15 px-1 text-foreground">{ex || p}</mark>;
      })}
    </p>
  );
}

export function LibraryDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [language, setLanguage] = useState('pt_BR');
  const [cat, setCat] = useState<Cat>('UTILITY');
  const [picked, setPicked] = useState<LibTemplate | null>(null);
  const [name, setName] = useState('');
  const [inputs, setInputs] = useState<Record<number, string>>({});
  const { data: otpInfo } = useOTPInfo();
  const authOpen = otpReleased(otpInfo?.eligibility);

  const { data, isFetching, error } = useQuery({
    queryKey: ['templates', 'library', query, language],
    enabled: open,
    queryFn: async (): Promise<LibTemplate[]> => {
      const qs = new URLSearchParams({ language, ...(query ? { search: query } : {}) });
      const r = await apiFetch('GET', `/templates/library?${qs}`);
      if (!r.ok) throw new Error((r.data as { message?: string })?.message ?? t('templates.library.loadFailed'));
      return (r.data as { templates?: LibTemplate[] })?.templates ?? [];
    },
  });
  const items = (data ?? []).filter((x) => x.category?.toUpperCase() === cat);

  // Botões que pedem dado do cliente: o link do site (URL) e o telefone.
  const needs = (picked?.buttons ?? [])
    .map((b, i) => ({ b, i }))
    .filter(({ b }) => ['URL', 'PHONE_NUMBER'].includes(b.type?.toUpperCase()));

  const create = useMutation({
    mutationFn: async () => {
      if (!picked) return null;
      const button_inputs = needs.map(({ b, i }) => b.type.toUpperCase() === 'URL'
        ? { type: 'url', url: inputs[i] ?? '' }
        : { type: 'phone_number', phone: inputs[i] ?? '' });
      const r = await apiFetch('POST', '/templates/library', {
        library_template_name: picked.name, name: name || picked.name, language,
        ...(button_inputs.length ? { button_inputs } : {}),
      });
      if (!r.ok) throw new Error((r.data as { message?: string })?.message ?? t('templates.createFailed'));
      return (r.data as { template?: { status?: string } })?.template ?? null;
    },
    onSuccess: (tpl) => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      toast.success(tpl?.status === 'APPROVED' ? t('templates.library.createdApproved') : t('templates.submitted'));
      close();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function close() {
    onOpenChange(false);
    setPicked(null); setName(''); setInputs({});
  }

  function pick(it: LibTemplate) {
    setPicked(it); setName(it.name); setInputs({});
  }

  const missing = needs.some(({ i }) => !(inputs[i] ?? '').trim());

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('templates.library.title')}</DialogTitle>
          <DialogDescription>{t('templates.library.subtitle')}</DialogDescription>
        </DialogHeader>

        {!picked ? (
          <div className="flex flex-col gap-3">
            <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); }}>
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                <Input className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('templates.library.search')} aria-label={t('templates.library.search')} />
              </div>
              <select value={language} onChange={(e) => setLanguage(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm" aria-label={t('templates.field.language')}>
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <Button type="submit" variant="outline">{t('templates.library.searchAction')}</Button>
            </form>

            <div className="flex gap-2">
              {(['UTILITY', 'AUTHENTICATION'] as const).map((c) => (
                <button key={c} type="button" onClick={() => setCat(c)}
                  disabled={c === 'AUTHENTICATION' && !authOpen}
                  className={cn('rounded-full border px-3 py-1 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                    cat === c ? 'border-primary bg-primary/10 text-foreground' : 'border-input text-muted-foreground hover:text-foreground')}>
                  {t(`templates.category.${c}`)}
                </button>
              ))}
            </div>
            {!authOpen && <p className="text-xs text-muted-foreground">{t('templates.library.authLocked')}</p>}

            {isFetching ? (
              <div className="flex justify-center py-10"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>
            ) : error ? (
              <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{(error as Error).message}</p>
            ) : items.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('templates.library.empty')}</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 *:min-w-0">
                {items.map((it) => (
                  <button key={it.id || it.name} type="button" onClick={() => pick(it)}
                    className="flex flex-col gap-2 rounded-lg border p-3 text-left transition-colors hover:border-primary hover:bg-primary/5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-xs font-medium">{it.name}</span>
                      {it.topic && <Badge variant="secondary" className="text-[10px]">{it.topic}</Badge>}
                    </div>
                    {it.header && <p className="text-xs font-medium text-muted-foreground">{it.header}</p>}
                    <BodyPreview body={it.body} params={it.body_params} />
                    {!!it.buttons?.length && (
                      <div className="flex flex-wrap gap-1">
                        {it.buttons.map((b, i) => (
                          <span key={i} className="rounded border px-1.5 py-0.5 text-[11px] text-primary">{b.text || b.type}</span>
                        ))}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border bg-muted/30 p-3">
              <BodyPreview body={picked.body} params={picked.body_params} />
            </div>
            <div>
              <Label htmlFor="lib-name">{t('templates.field.name')}</Label>
              <Input id="lib-name" value={name} onChange={(e) => setName(e.target.value.toLowerCase())} />
              <p className="mt-1 text-xs text-muted-foreground">{t('templates.field.nameHint')}</p>
            </div>
            {needs.map(({ b, i }) => (
              <div key={i}>
                <Label htmlFor={`lib-btn-${i}`}>
                  {b.type.toUpperCase() === 'URL'
                    ? t('templates.library.urlFor', { button: b.text ?? '' })
                    : t('templates.library.phoneFor', { button: b.text ?? '' })}
                </Label>
                <Input id={`lib-btn-${i}`} value={inputs[i] ?? ''}
                  onChange={(e) => setInputs({ ...inputs, [i]: e.target.value })}
                  placeholder={b.type.toUpperCase() === 'URL' ? 'https://sualoja.com.br/pedido' : '+5511999999999'} />
              </div>
            ))}
            <p className="flex items-start gap-2 rounded-md bg-emerald-500/10 p-3 text-xs text-emerald-800 dark:text-emerald-300">
              <Zap className="mt-0.5 size-3.5 shrink-0" />{t('templates.library.instant')}
            </p>
          </div>
        )}

        <DialogFooter>
          {picked
            ? <Button variant="outline" onClick={() => setPicked(null)}>{t('templates.library.back')}</Button>
            : <Button variant="outline" onClick={close}>{t('common.cancel')}</Button>}
          {picked && (
            <Button onClick={() => create.mutate()} disabled={create.isPending || !name || missing}>
              {create.isPending && <Loader2 className="size-4 animate-spin" />}{t('templates.library.create')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
